import { Controller } from "@hotwired/stimulus"

/**
 * MediaBrowserSearch Controller
 *
 * Handles debounced search functionality in the media browser modal.
 * Submits search queries via Turbo Frame to update results without closing the modal.
 */
export default class extends Controller {
  static targets = ["input", "loadingIndicator"]

  connect() {
    this.searchTimeout = null
    this.debounceDelay = 2000 // milliseconds
  }

  disconnect() {
    // Clean up timeout if controller is disconnected
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  }

  /**
   * Handle search input with debounce
   * Waits for user to stop typing before submitting query
   */
  search(event) {
    // If Enter key is pressed, prevent form submission and search immediately
    if (event.type === "keydown" && event.key === "Enter") {
      event.preventDefault()
      this.submitSearch()
      return
    }

    // Clear existing timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    // Show loading indicator
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.remove("hidden")
    }

    // Set new timeout to submit search
    this.searchTimeout = setTimeout(() => {
      this.submitSearch()
    }, this.debounceDelay)
  }

  /**
   * Submit search via Turbo Frame
   * Constructs URL with query parameter and navigates frame
   */
  submitSearch() {
    const query = this.inputTarget.value.trim()

    // Build search URL with all necessary params
    const searchParams = new URLSearchParams()

    if (query) {
      searchParams.set("query", query)
    }

    // Preserve resource context params
    // Look for the submit controller's data attributes (they include "submit" in the name)
    const submitController = this.element.closest("[data-controller*='media-browser-submit']")
    const resourceType = submitController?.dataset.mediaBrowserSubmitResourceTypeValue
    const resourceId = submitController?.dataset.mediaBrowserSubmitResourceIdValue
    const attachmentName = submitController?.dataset.mediaBrowserSubmitAttachmentNameValue

    if (resourceType) searchParams.set("resource_type", resourceType)
    if (resourceId) searchParams.set("resource_id", resourceId)
    if (attachmentName) searchParams.set("attachment_name", attachmentName)

    // Use the media browser index path
    const searchUrl = `/media_browser?${searchParams.toString()}`

    // Find the turbo frame and navigate it
    const turboFrame = this.element.closest("turbo-frame")
    if (turboFrame) {
      turboFrame.src = searchUrl
    }

    // Hide loading indicator after a short delay
    setTimeout(() => {
      if (this.hasLoadingIndicatorTarget) {
        this.loadingIndicatorTarget.classList.add("hidden")
      }
    }, 500)
  }

  /**
   * Clear search input and reload all results
   */
  clearSearch() {
    this.inputTarget.value = ""
    this.submitSearch()
  }
}
